import { useAtomValue, useSetAtom } from "jotai";
import { type JSX, useRef, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Checkbox } from "@frontend/components/atoms/Checkbox";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import {
  ContactAddressModal,
  type ContactAddressModalHandle,
} from "@frontend/features/address-location";
import { useField } from "@frontend/hooks/form";
import { PickerField } from "@frontend/shared/picker-field";
import { ReferenceField } from "@frontend/shared/reference-field";
import { useLingui } from "@lingui/react/macro";

import {
  clientScopeAtom,
  optionsAtom,
  projectEditorAtom,
  projectForm,
  reloadPlacesAtom,
} from "../stores/editor";
import type { PickerOption } from "../types";
import { FieldActionButton } from "./FieldActionButton";
import { Section } from "./Section";

const fields = projectForm.fields;

const condition = fields.commercialCondition.fields;

const BILLING_PLACE = "BILLING";

/** How the client pays, and where a mailed bill goes. */
export function PaymentTermsSection(): JSX.Element {
  const { t } = useLingui();

  const options = useAtomValue(optionsAtom);

  const paymentEmail = useField(condition.paymentEmail);
  const paymentEmailCopyTo = useField(condition.paymentEmailCopyTo);

  return (
    <Section icon="ReceiptText" legend={t`Payment terms`} gutter={false}>
      <ReferenceField
        label={t`Type`}
        placeholder={t`Please choose an option`}
        items={options.paymentTypes}
        field={condition.paymentTypeId}
      />

      <ReferenceField
        label={t`Payment delay`}
        placeholder={t`Please choose an option`}
        items={options.paymentPeriods}
        field={condition.interventionPaymentPeriodId}
      />

      <Field>
        <FieldLabel>{t`Main email address`}</FieldLabel>

        <FieldContent>
          <Input
            type="email"
            value={paymentEmail.value ?? ""}
            onChange={(event) => paymentEmail.setValue(event.target.value)}
          />

          <FieldError match={Boolean(paymentEmail.error)}>
            {paymentEmail.error}
          </FieldError>
        </FieldContent>
      </Field>

      <Field>
        <FieldLabel>{t`Copy to`}</FieldLabel>

        <FieldContent>
          <Input
            value={paymentEmailCopyTo.value ?? ""}
            placeholder={t`Email addresses in copy`}
            onChange={(event) =>
              paymentEmailCopyTo.setValue(event.target.value)
            }
          />

          <FieldError match={Boolean(paymentEmailCopyTo.error)}>
            {paymentEmailCopyTo.error}
          </FieldError>
        </FieldContent>
      </Field>
    </Section>
  );
}

/** Who the bill is addressed to, and what it quotes back to them. */
export function RecipientSection(): JSX.Element {
  const { t } = useLingui();

  const scope = useAtomValue(clientScopeAtom);
  const { project } = useAtomValue(projectEditorAtom);
  const reloadPlaces = useSetAtom(reloadPlacesAtom);

  const billingAddress = useField(fields.billingAddressId);

  const [attached, setAttached] = useState<PickerOption | null>(null);

  const billingAddresses =
    null === attached ||
    scope.billingAddresses.some(({ id }) => id === attached.id)
      ? scope.billingAddresses
      : [attached, ...scope.billingAddresses];

  const addressModalRef = useRef<ContactAddressModalHandle>(null);

  const emptyAddresses =
    null === scope.contactId
      ? t`Choose a client first.`
      : 0 === billingAddresses.length
        ? t`This client has no address yet. Add one with the button beside.`
        : t`No match.`;

  const clientReference = useField(fields.billingClientReference);
  const inBill = useField(fields.billingClientReferenceInBill);

  return (
    <>
      <Section icon="Mail" legend={t`Bill recipient`}>
        <PickerField
          label={t`Billing address`}
          placeholder={t`Please choose an option`}
          items={billingAddresses}
          field={fields.billingAddressId}
          fallbackLabel={project?.billingAddressLabel}
          empty={emptyAddresses}
          popupWidth="content"
          gutterAction={
            <FieldActionButton
              icon="MapPinPlus"
              label={t`New address`}
              disabled={null === scope.contactId}
              onClick={(event) =>
                addressModalRef.current?.open(
                  scope.contactId ?? undefined,
                  BILLING_PLACE,
                  event.currentTarget,
                )
              }
            />
          }
          action={
            // No contact: the dialog asks which one the address is for,
            // which is how a bill goes somewhere else entirely.
            <Button
              variant="link"
              size="sm"
              leftSection={<Icon name="UserSearch" size="sm" />}
              onClick={(event) =>
                addressModalRef.current?.open(
                  undefined,
                  BILLING_PLACE,
                  event.currentTarget,
                )
              }
            >
              {t`For address, another contact`}
            </Button>
          }
        />

        <Field>
          <FieldLabel>{t`Billing reference`}</FieldLabel>

          <FieldContent>
            <Input
              value={clientReference.value ?? ""}
              onChange={(event) => clientReference.setValue(event.target.value)}
            />

            <Checkbox
              checked={inBill.value ?? false}
              label={t`Show on the bill`}
              onCheckedChange={(next) => inBill.setValue(Boolean(next))}
            />

            <FieldError match={Boolean(clientReference.error)}>
              {clientReference.error}
            </FieldError>
          </FieldContent>
        </Field>
      </Section>

      <ContactAddressModal
        ref={addressModalRef}
        onSubmit={(address) => {
          setAttached({
            id: address.id,
            label: addressLabel(address),
            hint: address.place,
          });
          billingAddress.setValue(address.id);

          // The client's own list may hold it too.
          void reloadPlaces();
        }}
      />
    </>
  );
}

/** What the dialog hands back: a typed address, or the details it edited. */
function addressLabel(
  address: Parameters<
    NonNullable<React.ComponentProps<typeof ContactAddressModal>["onSubmit"]>
  >[0],
): string {
  if ("displayAddress" in address) {
    return address.displayAddress ?? address.label ?? String(address.id);
  }

  const line = [address.street, address.postalCode, address.city]
    .filter(Boolean)
    .join(" ");

  return "" === line ? String(address.id) : line;
}
